import { storeToRefs } from 'pinia'

import { useDraftStore } from '~/stores/draft'
import { useCartPromoStore } from '~/stores/cartPromo'
import { useInfluencersStore } from '~/stores/influencers'
import { useMiscDraftProgressivePromosStore } from '~/stores/miscDraftProgressivePromos'
import { useSnackbarStore } from '~/stores/snackbar'

import type { StatsV3Influencer } from '~/types/influencer'

/**
 * Tracks cart additions and deletions and pops snackbars when needed
 */
export function useCartSnackbars() {
  const { $pinia } = useNuxtApp()
  const { t } = useI18n()
  const snackbarCartSubscribeHandler = ref<null | (() => void)>(null)

  const miscDraftProgressivePromosStore =
    useMiscDraftProgressivePromosStore($pinia)

  const draftStore = useDraftStore($pinia)
  const {
    id: DRAFT_ID,
    HAS_DRAFT: USER_CART_HAS_DRAFT,
    INFLUENCERS: CART,
  } = storeToRefs(draftStore)
  const { IS_ENABLED: IS_PROGRESSIVE_PROMOS_ENABLED } = storeToRefs(
    miscDraftProgressivePromosStore,
  )
  const { code: CART_PROMO_CODE, actions: CART_PROMO_ACTIONS } = storeToRefs(
    useCartPromoStore($pinia)
  )

  const { GET_BY_IDS_SORTED: GET_INFS_SORTED_BY_IDS } =
    useInfluencersStore($pinia)
  const { CREATE: CREATE_SNACKBAR } = useSnackbarStore($pinia)
  const { CHECK_FOR_PROGRESSIVE_PROMO } = draftStore
  const {
    PROGRESSIVE_PROMO_FROM_INF_COUNT: GET_PROGRESSIVE_PROMO_FROM_INF_COUNT,
  } = miscDraftProgressivePromosStore

  const cartLength = computed<number>(() => CART.value.length)
  watch(cartLength, (oldCartLength, newCartLength) => {
    displayPromotionalSnackbars(oldCartLength, newCartLength)
  })

  onMounted(() => {
    if (snackbarCartSubscribeHandler.value) return

    snackbarCartSubscribeHandler.value = draftStore.$onAction(
      ({ name, args, after }) => {
        const userHasNoDraftOrIsntPerformingCartAction =
          (name !== 'ADD_INF' && name !== 'REMOVE_INF') ||
          !USER_CART_HAS_DRAFT.value

        if (userHasNoDraftOrIsntPerformingCartAction) return

        after(() => {
          const payload: number[] =
            typeof args === 'number' ? ([args] as number[]) : (args as number[])

          if ((payload?.length ?? 0) === 1) {
            const influencer: StatsV3Influencer | undefined =
              GET_INFS_SORTED_BY_IDS(payload)[0]

            if (!influencer) return

            name === 'ADD_INF'
              ? displayInfAddedSnackbar(influencer)
              : displayInfRemovedSnackbar(influencer)
          }
        })
      },
    )
  })

  onBeforeUnmount(() => {
    if (snackbarCartSubscribeHandler.value) snackbarCartSubscribeHandler.value()

    snackbarCartSubscribeHandler.value = null
  })

  function displayInfAddedSnackbar(influencer: StatsV3Influencer): void {
    window.setTimeout(() => {
      CREATE_SNACKBAR({
        component: defineAsyncComponent(
          () => import('~/components/shared/snackbar/contentTemplate.vue'),
        ),
        class: { snackbarContentTemplate: true },
        duration: 3850,
        props: {
          icon: 'fas fa-check',
          influencerName: influencer.entity,
          link: {
            name: 'influencer-profile-slug',
            params: { slug: influencer.slug },
          },
          text: t('components.campaignFooter.snackbarCart.addedToCart'),
        },
        transitionName: 'slideFromBottom',
      })
    }, 150)
  }

  function displayInfRemovedSnackbar(influencer: StatsV3Influencer): void {
    window.setTimeout(() => {
      CREATE_SNACKBAR({
        component: defineAsyncComponent(
          () => import('~/components/shared/snackbar/contentTemplate.vue'),
        ),
        class: { snackbarContentTemplate: true },
        duration: 3850,
        props: {
          text: t('components.campaignFooter.snackbarCart.removedFromCart', {
            influencerName: influencer.entity,
          }),
          icon: 'fas fa-trash-alt',
        },
        transitionName: 'slideFromBottom',
      })
    }, 150)
  }

  function displayConfirmationSnackbar(discountPercent: number): void {
    window.setTimeout(() => {
      CREATE_SNACKBAR({
        component: defineAsyncComponent(
          () => import('~/components/shared/snackbar/contentTemplate.vue'),
        ),
        class: {
          'snackbarContentTemplate custom-bg animated-background-gradient':
            true,
        },
        props: {
          text: t(
            'components.campaignFooter.snackbarProgressivePromos.discountAcquired',
            { discountPercent },
          ),
          hasConfettiAnimation: true,
        },
        duration: 3850,
        transitionName: 'slideFromBottom',
      })
    }, 800)
  }

  async function displayPromotionalSnackbars(
    newCartLength: number,
    oldCartLength: number,
  ): Promise<void> {
    if (IS_PROGRESSIVE_PROMOS_ENABLED.value && DRAFT_ID.value) {
      const oldCartProgressivePromo =
        GET_PROGRESSIVE_PROMO_FROM_INF_COUNT(oldCartLength)
      const eligibleProgressivePromo = await CHECK_FOR_PROGRESSIVE_PROMO()

      // Let's check if user just obtained a promo
      if (
        eligibleProgressivePromo &&
        eligibleProgressivePromo.progressive_parameters.min_influencers <=
          cartLength.value &&
        newCartLength > oldCartLength && // Check if user ADDED (and not REMOVED) an influencer
        eligibleProgressivePromo.code !==
          (oldCartProgressivePromo?.code ?? null) &&
        eligibleProgressivePromo.code === CART_PROMO_CODE.value
      )
        displayConfirmationSnackbar(CART_PROMO_ACTIONS.value.campaign?.value)
    }
  }
}
